import { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { PROMPT_BEFORE_IDLE } from "./constants";
import { addMilliseconds } from "date-fns";

type UseInactivityTimerProps = {
  disabled: boolean;
  onAction: (lastActive: Date) => void;
  onInactive: () => void;
};

type UseInactivityTimer = {
  promptOpen: boolean;
  timeExpired: boolean;
  remainingSeconds: number;
  promptConfirm: () => void;
  onInactive: () => void;
  setTimeoutMs: (timeOutMs: number | undefined) => void;
};

export const defaultInactivityTimerValues: UseInactivityTimer = {
  promptOpen: false,
  timeExpired: false,
  remainingSeconds: 0,
  promptConfirm: () => {},
  onInactive: () => {},
  setTimeoutMs: () => {},
};

export function useInactivityTimer({
  disabled,
  onAction: onActionProp,
  onInactive,
}: UseInactivityTimerProps): UseInactivityTimer {
  const [promptOpen, setPromptOpen] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [timeoutMs, setTimeoutMs] = useState<number | undefined>();

  const onPrompt = () => {
    setPromptOpen(true);
  };

  const onActive = () => {
    setPromptOpen(false);
  };

  const onIdle = () => {
    setTimeExpired(true);
    onInactive();
  };

  const fireOnActionProp = () => {
    const time = getLastActiveTime();
    if (time && timeoutMs) {
      onActionProp(addMilliseconds(time, timeoutMs));
    }
  };

  const { activate, getRemainingTime, getLastActiveTime } =
    useIdleTimer({
      disabled,
      timeout: timeoutMs,
      promptBeforeIdle: PROMPT_BEFORE_IDLE,
      throttle: 2000,
      crossTab: true,
      leaderElection: true,
      onPrompt,
      onIdle,
      onActive,
      onAction: fireOnActionProp,
    });

  const promptConfirm = () => {
    setPromptOpen(false);
    activate();
  };

  const saveRemainingTime = () => setRemaining(getRemainingTime());

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout> | undefined;
    if (promptOpen) {
      saveRemainingTime();
      interval = setInterval(() => saveRemainingTime(), 500);
    }

    return () => {
      clearInterval(interval);
    };
  }, [promptOpen]);

  const remainingSeconds = timeExpired ? 0 : remaining;

  return {
    promptOpen,
    promptConfirm,
    timeExpired,
    remainingSeconds,
    onInactive,
    setTimeoutMs,
  };
}
